import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ExternalLink,
  Lightbulb,
  Linkedin,
  Menu,
  Target,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import type React from 'react';
import { useState } from 'react';

const AboutPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">
                BetterCapital.us
              </span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="/#features"
                className="text-gray-600 hover:text-gray-900"
              >
                Features
              </a>
              <a href="/#pricing" className="text-gray-600 hover:text-gray-900">
                Pricing
              </a>
              <a
                href="/#testimonials"
                className="text-gray-600 hover:text-gray-900"
              >
                Testimonials
              </a>
              <a href="/about" className="text-blue-600 font-medium">
                About
              </a>
              <Button
                variant="outline"
                className="mr-2"
                onClick={() => {
                  window.location.href = '/app';
                }}
              >
                Sign In
              </Button>
              <Button
                onClick={() => {
                  window.location.href = '/app';
                }}
              >
                Get Started
              </Button>
            </div>
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
                <a
                  href="/#features"
                  className="block px-3 py-2 text-gray-600 hover:text-gray-900"
                >
                  Features
                </a>
                <a
                  href="/#pricing"
                  className="block px-3 py-2 text-gray-600 hover:text-gray-900"
                >
                  Pricing
                </a>
                <a
                  href="/#testimonials"
                  className="block px-3 py-2 text-gray-600 hover:text-gray-900"
                >
                  Testimonials
                </a>
                <a
                  href="/about"
                  className="block px-3 py-2 text-blue-600 font-medium"
                >
                  About
                </a>
                <div className="px-3 py-2 space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      window.location.href = '/app';
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    className="w-full"
                    onClick={() => {
                      window.location.href = '/app';
                    }}
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              About BetterCapital.us
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              We're revolutionizing real estate investment through AI-driven
              analytics, empowering investors to unlock the true potential of
              their portfolios.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle>Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-center">
                  To democratize real estate investment by providing AI-powered
                  tools that help investors make smarter decisions and maximize
                  their returns.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="w-8 h-8 text-green-600" />
                </div>
                <CardTitle>Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-center">
                  A world where every real estate investor has access to
                  professional-grade analytics and insights to build wealth
                  through property investment.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
                <CardTitle>Our Values</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-center">
                  Innovation, transparency, and user success drive everything we
                  do. We believe in empowering investors with the tools they
                  need to succeed.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the visionaries behind BetterCapital.us, bringing together
              decades of experience in real estate, technology, and investment
              management.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* CEO */}
            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">BS</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Bobby Sharma</h3>
                  <p className="text-blue-600 font-medium mb-4">
                    Chief Executive Officer
                  </p>
                  <p className="text-gray-600 mb-4">
                    Bobby brings extensive experience in real estate investment
                    and financial services. As the founder of BetterCapital.us,
                    he has helped thousands of investors optimize their
                    portfolios and maximize returns through innovative
                    investment strategies.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <a
                      href="https://www.linkedin.com/in/bayflip/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                    >
                      <Linkedin className="w-5 h-5" />
                      <span>LinkedIn</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <a
                      href="https://calendly.com/bayflip/30min-1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-green-600 hover:text-green-800"
                    >
                      <span>Schedule Meeting</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTO */}
            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">RV</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    Ram Vaidyanathan
                  </h3>
                  <p className="text-green-600 font-medium mb-4">
                    Chief Technology Officer
                  </p>
                  <p className="text-gray-600 mb-4">
                    Ram leads our technology vision and development, bringing
                    deep expertise in AI, machine learning, and scalable
                    software architecture. His technical leadership ensures our
                    platform delivers cutting-edge analytics and seamless user
                    experiences.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <a
                      href="https://www.linkedin.com/in/ramkumarv/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-green-600 hover:text-green-800"
                    >
                      <Linkedin className="w-5 h-5" />
                      <span>LinkedIn</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Strategic Partners
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We've partnered with industry leaders to provide our users with
              exclusive access to premium tools and resources.
            </p>
          </div>

          <div className="grid md:grid-cols-1 gap-8 max-w-4xl mx-auto">
            {/* FlipSystem */}
            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">FS</span>
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-semibold mb-2">FlipSystem</h3>
                    <p className="text-orange-600 font-medium mb-2">
                      Founded by Eric Martel
                    </p>
                    <p className="text-gray-600 mb-4">
                      FlipSystem is a comprehensive real estate investment
                      platform that provides tools, resources, and community
                      support for property investors. Our partnership gives
                      BetterCapital users exclusive access to FlipSystem's
                      advanced analytics and deal analysis tools.
                    </p>
                    <div className="flex justify-center md:justify-start space-x-4">
                      <a
                        href="https://flipsystem.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-orange-600 hover:text-orange-800"
                      >
                        <span>Visit FlipSystem</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <a
                        href="https://www.linkedin.com/in/ericmartelofficial/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                      >
                        <Linkedin className="w-5 h-5" />
                        <span>Eric's LinkedIn</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Partnership Benefits */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-center mb-8">
              Partnership Benefits
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-0 shadow-lg">
                <CardHeader className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <CardTitle>Exclusive Discounts</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-center">
                    Get 10-20% off FlipSystem tools and MTK Buyers Club
                    memberships with your BetterCapital subscription.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Premium Access</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-center">
                    Unlock exclusive features and early access to new tools from
                    our partner ecosystem.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Lightbulb className="w-6 h-6 text-purple-600" />
                  </div>
                  <CardTitle>Integrated Workflow</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-center">
                    Seamlessly connect your BetterCapital data with partner
                    tools for enhanced analysis and reporting.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Our Impact
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Numbers that speak to our commitment to real estate investors
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                15+
              </div>
              <div className="text-blue-100">KPI Metrics Tracked</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                100+
              </div>
              <div className="text-blue-100">Properties Managed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                $50M+
              </div>
              <div className="text-blue-100">Portfolio Value Tracked</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                99.9%
              </div>
              <div className="text-blue-100">Uptime Guarantee</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Join Our Mission?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Become part of a community of successful real estate investors who
            trust BetterCapital.us to optimize their portfolios and maximize
            returns.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="text-lg px-8 py-6"
              onClick={() => {
                window.location.href = '/app';
              }}
            >
              Start Your Free Trial
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6"
              onClick={() =>
                window.open('https://calendly.com/bayflip/30min-1', '_blank')
              }
            >
              Schedule a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">BetterCapital.us</span>
              </div>
              <p className="text-gray-400">
                The AI-powered platform for real estate investors who want to
                maximize their returns.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="/#features" className="hover:text-white">
                    Features
                  </a>
                </li>
                <li>
                  <a href="/#pricing" className="hover:text-white">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="/about" className="hover:text-white">
                    About Us
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="/about" className="hover:text-white">
                    About
                  </a>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Blog
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Help Center
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Documentation
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-white text-left">
                    Status
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 BetterCapital.us. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
